/**
 * 失败响应的**统一出口**：把任何异常整理成"界面上能直接显示的一句话"。
 *
 * ## 为什么单独一个模块（2026-10-18 的实测）
 *
 * 用户报告：管理员登录页**不填密码**直接点「登录」，屏幕上冒出一串形如
 * `[{"code":"too_small",...,"message":"请输入密码","path":["password"]}]` 的 JSON。
 *
 * 链路（每一步都能在代码里对上）：
 *
 *  1. `apps/api/src/app.ts` 里业务路由注册在 `app.register(async (v1) => …, { prefix })`
 *     的**子上下文**中，而 `registerErrorHandler(app)` 写在这句**之后**。
 *     Fastify 在注册路由时就把当时的错误处理器绑定到路由上 —— 之后在根实例上
 *     `setErrorHandler` 不会回头改已注册的路由。于是 `/api/v1/auth/login` 落到
 *     **Fastify 默认错误处理器**。
 *  2. `loginSchema.parse()` 抛 `ZodError`（空密码 → `password: z.string().min(1)`）。
 *     zod 3 把 `ZodError.message` 设成 **issues 的 JSON 字符串**；默认处理器对没有
 *     `statusCode` 的异常按 **500** 处理，响应体是
 *     `{ statusCode:500, error:"Internal Server Error", message:"[…]" }`。
 *  3. 前端 `lib/api/client.ts` 的 `pickError()` 把这个顶层 `message` 原样塞进
 *     `ApiError`；`toErrorMessage()` 又原样返回 ⇒ 登录页把 JSON 渲染在 `role="alert"` 里。
 *
 * 所以"界面不出 JSON"不能靠每个页面自觉。这里提供**共用**的归一化：
 *  · `describeApiFailure()` —— 听写模块先用的那套（message / details / zod JSON）；
 *  · `describeFailureMessage()` —— 只要一句人话，给表单页（登录、动态口令绑定）用；
 *  · `toErrorMessage()`（见 `./error.ts`）也改成走这里，于是**所有**页面一起受益。
 *
 * `./dictation.ts` 只把名字再导出一次，既有 import（页面、测试）保持不动。
 */
import { ERROR_CODES, errorMessage } from '@fht/shared';

/** 拿不到人话时的兜底。 */
export const DEFAULT_FAILURE_MESSAGE = '操作失败，请稍后再试';

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** 可选数字：缺失 / null / 非法一律返回 null（与"0"区分开）。 */
function toOptionalNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

// ---------------------------------------------------------------- 类型

/** 失败响应里**一条具体原因**（`error.details` 归一后的形状，与导入的 `ImportIssueView` 同形）。 */
export interface FailureDetail {
  /** 行号（`>0`）；整份内容级 / 字段级的原因没有行号 ⇒ `null`。 */
  line: number | null;
  /** 原文摘要（拿不到时是空串）；zod 的 details 里放的是字段路径。 */
  raw: string;
  /** 出问题的那个**词元**（拿不到时 `null`）。 */
  token: string | null;
  message: string;
}

/** 把 `ApiError` / 任意异常整理后的结果。 */
export interface ApiFailure {
  /** HTTP 状态码。 */
  status: number;
  /** 后端错误码（拿不到时为 `null`）。 */
  code: string | null;
  /** 给用户看的一句话（**永远不会是 JSON**）。 */
  message: string;
  /** 原始 message 是否是 zod 的 JSON 串（说明这是后端校验层漏出的 500）。 */
  zodMessage: boolean;
  /** `error.details` 里能读出来的每一条具体原因（读不出来时是空数组）。 */
  details: FailureDetail[];
}

/** 旧名字（听写模块先用的）：保留别名，避免改一堆既有 import。 */
export type DictationFailure = ApiFailure;

// ---------------------------------------------------------------- JSON 识别与降级

/**
 * 这串文本是不是"被 stringify 过的对象/数组/字符串"？
 *
 * 只认 `{…}` / `[…]` / `"…"` 开头且能 `JSON.parse` 成 object/string 的文本 ——
 * 正常的中文文案（"请填写密码"、"服务器开小差了…"）一定不会被误判。
 */
function looksLikeJson(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed === '') return false;
  const first = trimmed[0];
  if (first !== '{' && first !== '[' && first !== '"') return false;
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed === null) return false;
    return typeof parsed === 'object' || typeof parsed === 'string';
  } catch {
    return false;
  }
}

/**
 * 从 JSON 结构里**只挑人话**：优先 `message` / `reason`，其次 `error` 等。
 *
 * 之所以不"整块 JSON.stringify 出来给用户看"：那种东西对家长/管理员没有任何信息量，
 * 而其中真正有用的往往只是 `message` 那一句中文。
 */
const HUMAN_TEXT_KEYS = ['message', 'reason', 'error', 'msg', 'detail', 'description'] as const;

function collectHumanText(value: unknown, depth: number): string[] {
  if (depth > 4 || value === null || value === undefined) return [];
  if (typeof value === 'string') {
    const text = value.trim();
    if (text === '') return [];
    if (looksLikeJson(text)) {
      try {
        return collectHumanText(JSON.parse(text) as unknown, depth + 1);
      } catch {
        return [];
      }
    }
    return [text];
  }
  if (Array.isArray(value)) return value.flatMap((item) => collectHumanText(item, depth + 1));
  if (!isRecord(value)) return [];
  for (const key of HUMAN_TEXT_KEYS) {
    if (!(key in value)) continue;
    const found = collectHumanText(value[key], depth + 1);
    if (found.length > 0) return found;
  }
  return [];
}

/** 从一串 JSON 里取出人话；取不到返回 null（**绝不回落成原始 JSON**）。 */
function humanFromJson(text: string): string | null {
  if (!looksLikeJson(text)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return null;
  }
  const parts = collectHumanText(parsed, 0);
  return parts.length > 0 ? parts.join('；') : null;
}

/**
 * 把"某段文本"变成能显示的一句话：正常文本原样（去首尾空白），
 * JSON 只取里面的人话，取不到就返回空串（由调用方决定兜底文案）。
 */
function humanizeText(text: string): string {
  const trimmed = text.trim();
  if (trimmed === '') return '';
  if (!looksLikeJson(trimmed)) return trimmed;
  return humanFromJson(trimmed) ?? '';
}

// ---------------------------------------------------------------- details 归一

/**
 * 「这条原因说的是哪一处」——原文摘要（`raw`），拿不到时退回 zod 的**字段路径**。
 */
function rawOf(details: Record<string, unknown>): string {
  const raw = details['raw'];
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim();
  const path = details['path'];
  if (typeof path === 'string') return path.trim();
  if (Array.isArray(path)) return path.filter((part) => typeof part === 'string').join('.');
  return '';
}

/**
 * 把 `error.details` 归一成"一条条能显示的原因"。
 *
 * 要兼容本项目真实出现过的**四种**形状（每一种都在生产里回过）：
 *
 * | 来源 | 形状 | 归一成 |
 * | --- | --- | --- |
 * | 导入的逐行问题 | `[{line, raw, token, message}]` | 原样（行号 + 词元 + 原文都在） |
 * | zod 校验 | `[{path, message}]` | `raw` 放字段路径 |
 * | `resolveTextbook` 这类字段级 | `{field, reason}` | 一句 `reason`（没有 reason 时说清是哪个字段） |
 * | 其它对象 / 字符串 | `{…}` / `"…"` | 把能读的标量渲染成 `k=v`，**绝不整块丢掉** |
 *
 * ⚠️ 返回空数组 = "后端真的没给细节"（那时界面只能显示那句通用文案，
 *    这是诚实的结果，不是兜底）。
 */
export function readFailureDetails(details: unknown): FailureDetail[] {
  if (details === null || details === undefined) return [];
  if (typeof details === 'string') {
    const text = humanizeText(details);
    return text === '' ? [] : [{ line: null, raw: '', token: null, message: text }];
  }
  if (Array.isArray(details)) return details.flatMap((item) => readFailureDetails(item));
  if (!isRecord(details)) return [];

  const line = toOptionalNumber(details['line']);
  const lineOrNull = line !== null && line > 0 ? line : null;
  const token =
    typeof details['token'] === 'string' && details['token'] !== '' ? details['token'] : null;

  const message = typeof details['message'] === 'string' ? humanizeText(details['message']) : '';
  if (message !== '') {
    return [{ line: lineOrNull, raw: rawOf(details), token, message }];
  }

  /** `{ field, reason }`：后端把"哪个字段、为什么"分开写（`AppError` 的通用 message 盖不到它）。 */
  const field = typeof details['field'] === 'string' ? details['field'].trim() : '';
  const reason = typeof details['reason'] === 'string' ? humanizeText(details['reason']) : '';
  if (reason !== '' || field !== '') {
    return [
      {
        line: lineOrNull,
        raw: field,
        token,
        message: reason !== '' ? reason : `字段「${field}」没有通过校验`,
      },
    ];
  }

  /**
   * 兜底：把 details 里**能读的标量**渲染出来。
   * 宁可给一句 `textbook=人教版、edition=2024年8月第1版`，也不要把整块细节丢掉。
   * 字符串值同样过一遍 `humanizeText`（万一某个字段本身是被 stringify 的对象）。
   */
  const pairs = Object.entries(details)
    .filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value))
    .map(([key, value]) =>
      `${key}=${typeof value === 'string' ? humanizeText(value) : String(value)}`,
    )
    .filter((pair) => !pair.endsWith('='));
  return pairs.length === 0 ? [] : [{ line: null, raw: '', token: null, message: pairs.join('、') }];
}

// ---------------------------------------------------------------- zod 的 JSON message

/**
 * 把 zod 的 `message`（**JSON 字符串**）尽量翻成人话。
 *
 * 生产实测：`PUT .../hanzi/:id/pinyin` 传 `Hang2` → 500，`message` 是
 * `[{"validation":"regex","code":"invalid_string","message":"拼音只能用小写字母加声调符号，不能有大写、数字或空格","path":["pinyin"]}]`。
 * 其中的 `message` 本身就是**中文**，直接取出来用；取不到才回落原文。
 */
export function parseZodMessage(message: string): string | null {
  const text = message.trim();
  if (!text.startsWith('[')) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const messages = parsed
    .filter(isRecord)
    .map((issue) => (typeof issue['message'] === 'string' ? issue['message'] : ''))
    .filter((m) => m !== '');
  if (messages.length === 0) return null;
  return messages.join('；');
}

/**
 * 「这句话是**按错误码给的通用文案**吗」——用来判断"有没有更具体的话可以说"。
 *
 * 直接拿 `@fht/shared` 的 `ERROR_CODES` + `errorMessage()` 现算，**不抄一份字面量**：
 * 抄一份的话，后端改了文案这里就对不上，而它恰恰是"要不要用 details 里的具体原因"
 * 那个开关（对不上 = 又退回到笼统那句话）。
 */
const GENERIC_ERROR_MESSAGES: ReadonlySet<string> = new Set(
  Object.values(ERROR_CODES).map((code) => errorMessage(code)),
);

// ---------------------------------------------------------------- 统一出口

export interface DescribeFailureOptions {
  /** 拿不到人话时的兜底（默认 `DEFAULT_FAILURE_MESSAGE`）。 */
  fallback?: string;
  /**
   * zod 漏成 500 时，是否附上"这是后端校验未拦住的缺陷"的诊断后缀。
   * 默认**附上**（听写导入等界面需要它来区分"用户输入错"与"后端缺陷"）；
   * 登录 / 动态口令这类"最后一公里"的表单页关掉它，只留那句人话。
   */
  diagnostics?: boolean;
  /**
   * 通用错误码文案 + `details` 里有具体原因时，是否用具体原因盖过通用文案。
   * 默认 `true`（听写模块的修复落点）；`toErrorMessage` 这类通用出口用 `false`，
   * 避免把 `{field:'studentId'}` 这种开发期表述顶到用户脸上。
   */
  preferDetails?: boolean;
}

/**
 * 把 `ApiError` / 任意异常整理成"界面上要说的话"。
 *
 * 三步：
 *   1. zod 漏成 500（message 是 issue 数组的 JSON）→ 取里面的中文；
 *   2. 其它 JSON（Fastify 默认体整块被 stringify、后端把 details 塞进 message 等）
 *      → 只取 `message`/`reason`，取不到就用兜底，**绝不整串显示**；
 *   3. 普通文案 → 原样（通用文案 + details 有具体原因时，按 `preferDetails` 取舍）。
 */
export function describeApiFailure(
  error: unknown,
  options: DescribeFailureOptions = {},
): ApiFailure {
  const fallback = options.fallback ?? DEFAULT_FAILURE_MESSAGE;
  const diagnostics = options.diagnostics ?? true;
  const preferDetails = options.preferDetails ?? true;

  const record = isRecord(error) ? error : {};
  const status = toOptionalNumber(record['status']) ?? 0;
  const rawCode = record['code'];
  const code = typeof rawCode === 'string' && rawCode !== '' ? rawCode : null;
  const rawMessage = record['message'];
  const message = typeof rawMessage === 'string' ? rawMessage : '';
  const details = readFailureDetails(record['details']);

  const zod = parseZodMessage(message);
  if (zod !== null) {
    return {
      status,
      code,
      // 后端这一层漏成了 500，但 issue 里的中文是可以直接给用户看的
      message: diagnostics
        ? `${zod}（接口返回了 ${status || '?'}，这是后端校验未拦住的缺陷，已记录）`
        : zod,
      zodMessage: true,
      details,
    };
  }

  /** 非 zod 的 JSON：只取人话；取不到就兜底（不允许把 `{…}` 端给用户）。 */
  if (looksLikeJson(message)) {
    const human = humanFromJson(message);
    return {
      status,
      code,
      message: human ?? fallback,
      // 不是 zod 的 issue 数组，只是同样被 stringify 过 —— 不算"zod 漏出"
      zodMessage: false,
      details,
    };
  }

  /**
   * ⚠️ **具体原因要盖过笼统兜底**（听写模块"报错太笼统"的修复落点）。
   *
   * 后端的 `AppError` 永远把 `message` 设成**按错误码给的通用文案**，真正的原因
   * 只在 `details` 里 —— 所以当拿到的那句话是通用文案、而 `details` 里有具体原因时，
   * **用具体原因当这句话**。
   */
  const generic = message === '' || GENERIC_ERROR_MESSAGES.has(message);
  const specific = details.find((detail) => detail.message.trim() !== '');
  const headline =
    generic && preferDetails && specific !== undefined
      ? specific.message
      : message !== ''
        ? message
        : fallback;

  return {
    status,
    code,
    message: looksLikeJson(headline) ? fallback : headline,
    zodMessage: false,
    details,
  };
}

/**
 * 只要**一句**给用户看的话（表单页 / toast 用），保证不是 JSON。
 *
 * 与 `describeApiFailure()` 共用同一套识别逻辑，只是：
 *  · 不附 zod 的诊断后缀（用户不关心"后端校验没拦住"）；
 *  · 拿不到人话时用调用方给的 `fallback`（例如「登录失败，请重试」）。
 */
export function describeFailureMessage(error: unknown, fallback: string): string {
  const { message } = describeApiFailure(error, {
    fallback,
    diagnostics: false,
    preferDetails: false,
  });
  const text = message.trim();
  if (text === '' || looksLikeJson(text)) return fallback;
  return text;
}
